import React, { useState, useEffect } from 'react';
import { Typography, Button } from 'components/atoms';
import ReactStars from 'react-rating-stars-component';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { getGoalReview, addGoalReview } from 'store/Admin';
import { ApiResponseStatus } from 'models/enums';
import { useCurrentEmployeeId } from 'services/hooks';
import { request } from 'services/apis';
import { toast } from 'react-toastify';
import './index.scss';

interface Review {
    id: string;
    comment: string;
    rating: number;
    reviewedAt: string;
    reviewedBy: string;
    goalId?: string;
}

interface SubTask {
    id: string;
    title: string;
    description: string;
    deadline: string;
    status: 'Completed' | 'Pending' | 'In Progress';
    priority: 'High' | 'Medium' | 'Low';
    quarter?: string; 
}

interface Goal {
    id: string;
    title: string;
    description: string;
    priority: 'High' | 'Medium' | 'Low';
    status: 'Completed' | 'Pending' | 'In Progress';
    reviews: Review[];
    assignedTo: string;
    quarter: string;
    subtasks: SubTask[];
    isParentTask?: boolean;
    combinedGoalId?: string;
    acceptanceStatus?: string | null;
}

interface ReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: Goal | null;
    onSubmit: (review: { comment: string; rating: number }) => void;
    goalAssignmentGoalId?: string; 
    reviewerRole: 'Supervisor' | 'HR'; 
}

export const ReviewModal: React.FC<ReviewModalProps> = ({ isOpen, onClose, task, onSubmit, goalAssignmentGoalId, reviewerRole }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { goalReviews, addGoalReviewStatus } = useSelector((state: RootState) => state.admin);
    const { employeeId: reviewerId, isLoading: reviewerIdLoading } = useCurrentEmployeeId();
    
    const [comment, setComment] = useState('');
    const [rating, setRating] = useState(0);
    const [selectedQuarter, setSelectedQuarter] = useState('all');
    const [isAnimating, setIsAnimating] = useState(false);
    const [isResettingAcceptance, setIsResettingAcceptance] = useState(false);

    
    useEffect(() => {
        if (isOpen) {
            setIsAnimating(true);
            
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    
    useEffect(() => {
        if (isOpen && goalAssignmentGoalId) {
            dispatch(getGoalReview(goalAssignmentGoalId));
        }
    }, [isOpen, goalAssignmentGoalId, dispatch]);

    
    useEffect(() => {
        const currentUserReview = goalReviews
            .filter(review => review.goalAssignmentGoalId === goalAssignmentGoalId && review.reviewerId === reviewerId)
            .sort((a, b) => new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime())[0];
        
        if (currentUserReview) {
            setComment(currentUserReview.reviewText);
            setRating(currentUserReview.rating);
        } else {
            setComment('');
            setRating(0);
        }
    }, [goalReviews, goalAssignmentGoalId, reviewerId]);

    
    useEffect(() => {
        if (!isOpen) {
            setComment('');
            setRating(0);
            setSelectedQuarter('all');
        }
    }, [isOpen]);

    const handleResetAcceptanceStatus = async () => {
        if (!goalAssignmentGoalId || reviewerRole !== 'HR') return;
        
        setIsResettingAcceptance(true);
        
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            
            await request.post(`/GoalAssignment/update-goal-acceptance/${goalAssignmentGoalId}`, 
                JSON.stringify("Pending"), 
                config
            );
            
            toast.success('Acceptance status reset to Pending successfully!');
            
            onClose();
            
        } catch (error: any) {
            console.error('Error resetting acceptance status:', error);
            const errorMessage = error?.response?.data?.message || 'Failed to reset acceptance status. Please try again.';
            toast.error(errorMessage);
        } finally {
            setIsResettingAcceptance(false);
        }
    };

    if (!isOpen || !task) return null;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed':
                return '#52c41a';
            case 'In Progress':
                return '#faad14';
            case 'Pending':
                return '#ff4d4f';
            default:
                return '#8c8c8c';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'High':
                return '#ff4d4f';
            case 'Medium':
                return '#faad14';
            case 'Low':
                return '#52c41a';
            default:
                return '#8c8c8c';
        }
    };

    const getAcceptanceStatusColor = (status: string | null | undefined) => {
        switch (status) {
            case 'Accepted':
                return '#52c41a';
            case 'Declined':
                return '#ff4d4f';
            case 'Pending':
            default:
                return '#faad14';
        }
    };

    const getAcceptanceStatusText = (status: string | null | undefined) => {
        if (!status || status === 'Pending') return 'Pending Acceptance';
        return status;
    };

    const handleSubmit = async () => {
        if (!comment || rating === 0) {
            alert('Please provide both a comment and rating');
            return;
        }
        
        if (!goalAssignmentGoalId) {
            alert('Goal assignment ID is required');
            return;
        }

        
        if (!reviewerId) {
            alert('User information is required to submit review');
            return;
        }

        try {
            const reviewData = {
                reviewerId: reviewerId,
                reviewerRole: reviewerRole, 
                reviewText: comment,
                rating: rating
            };

            await dispatch(addGoalReview({ 
                goalAssignmentGoalId, 
                reviewData 
            })).unwrap();

            // Refresh the reviews after adding a new one
            await dispatch(getGoalReview(goalAssignmentGoalId));

            
            onSubmit({ comment, rating });
            
            setRating(0);
            onClose();
        } catch (error) {
            console.error('Failed to submit review:', error);
        }
    };

    
    const getAvailableQuarters = () => {
        const quarters = new Set<string>();
        task.subtasks.forEach(subtask => {
            if (subtask.quarter) {
                quarters.add(subtask.quarter);
            }
        });
        return Array.from(quarters).sort();
    };

    
    const filteredSubtasks = selectedQuarter === 'all' 
        ? task.subtasks 
        : task.subtasks.filter(subtask => subtask.quarter === selectedQuarter);

    const getSubtasksSummary = () => {
        const total = filteredSubtasks.length;
        const completed = filteredSubtasks.filter(st => st.status === 'Completed').length;
        const inProgress = filteredSubtasks.filter(st => st.status === 'In Progress').length;
        const pending = filteredSubtasks.filter(st => st.status === 'Pending').length;
        const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { total, completed, inProgress, pending, completionPercentage };
    };

    const subtasksSummary = getSubtasksSummary();
    const availableQuarters = getAvailableQuarters();

    const handleClose = () => {
        setIsAnimating(true);
        setTimeout(() => {
            onClose();
            setIsAnimating(false);
        }, 300); 
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    return (
        <div className={`review-modal-overlay ${isAnimating ? 'animating' : 'open'}`} onClick={handleOverlayClick}>
            <div className={`review-modal ${isAnimating ? 'animating' : 'open'}`}>
                <div className="review-modal__header">
                    <Typography type="heading4" text={task.title} />
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>

                <div className="review-modal__content">
                    <div className="task-details">
                        <div className="task-info">
                            <div className="info-row">
                                <Typography type="base_medium" text="Status:" />
                                <span className="status-badge" style={{ backgroundColor: getStatusColor(task.status) }}>
                                    {task.status}
                                </span>
                            </div>
                            <div className="info-row">
                                <Typography type="base_medium" text="Priority:" />
                                <span className="priority-badge" style={{ backgroundColor: getPriorityColor(task.priority) }}>
                                    {task.priority}
                                </span>
                            </div>
                            <div className="info-row">
                                <Typography type="base_medium" text="Acceptance:" />
                                <div className="acceptance-status-container">
                                    <span className="acceptance-badge" style={{ backgroundColor: getAcceptanceStatusColor(task.acceptanceStatus) }}>
                                        {getAcceptanceStatusText(task.acceptanceStatus)}
                                    </span>
                                    {reviewerRole === 'HR' && task.acceptanceStatus && task.acceptanceStatus !== 'Pending' && (
                                        <button 
                                            className="reset-acceptance-btn"
                                            onClick={handleResetAcceptanceStatus}
                                            disabled={isResettingAcceptance}
                                            title="Reset acceptance status to Pending"
                                        >
                                            {isResettingAcceptance ? 'Resetting...' : 'Reset Acceptance Status'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="task-description">
                            <Typography type="base_medium" text="Description:" />
                            <Typography type="base_regular" text={task.description} />
                        </div>

                        <div className="task-progress">
                            <Typography type="base_medium" text="Progress" />
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill"
                                    style={{ width: `${subtasksSummary.completionPercentage}%` }}
                                />
                                <Typography 
                                    type="small_medium" 
                                    text={`${subtasksSummary.completionPercentage}%`}
                                    className="progress-text"
                                />
                            </div>
                            <div className="progress-stats">
                                <div className="stat-item">
                                    <Typography type="small_medium" text="Total:" />
                                    <Typography type="base_medium" text={subtasksSummary.total.toString()} />
                                </div>
                                <div className="stat-item completed">
                                    <Typography type="small_medium" text="Completed:" />
                                    <Typography type="base_medium" text={subtasksSummary.completed.toString()} />
                                </div>
                                <div className="stat-item in-progress">
                                    <Typography type="small_medium" text="In Progress:" />
                                    <Typography type="base_medium" text={subtasksSummary.inProgress.toString()} />
                                </div>
                                <div className="stat-item pending">
                                    <Typography type="small_medium" text="Pending:" />
                                    <Typography type="base_medium" text={subtasksSummary.pending.toString()} />
                                </div>
                            </div>
                        </div>

                        <div className="subtasks-section">
                            <div className="subtasks-header">
                                <Typography type="base_medium" text="Subtasks" />
                                {availableQuarters.length > 0 && (
                                    <div className="quarter-filter">
                                        <Typography type="small_medium" text="Quarter:" />
                                        <select
                                            value={selectedQuarter}
                                            onChange={(e) => setSelectedQuarter(e.target.value)}
                                        >
                                            <option value="all">All Quarters</option>
                                            {availableQuarters.map(quarter => (
                                                <option key={quarter} value={quarter}>
                                                    {quarter}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="subtasks-list">
                                {filteredSubtasks.map(subtask => (
                                    <div key={subtask.id} className="subtask-item">
                                        <div className="subtask-header">
                                            <Typography type="base_regular" text={subtask.title} />
                                            <span 
                                                className="status-badge small"
                                                style={{ backgroundColor: getStatusColor(subtask.status) }}
                                            >
                                                {subtask.status}
                                            </span>
                                        </div>
                                        <div className="subtask-details">
                                            <Typography type="small_regular" text={subtask.description} />
                                            <div className="subtask-meta">
                                                <span 
                                                    className="priority-badge small"
                                                    style={{ backgroundColor: getPriorityColor(subtask.priority) }}
                                                >
                                                    {subtask.priority}
                                                </span>
                                                <div>
                                                    {subtask.quarter && (
                                                        <Typography 
                                                            type="small_regular" 
                                                            text={`${subtask.quarter} • `} 
                                                        />
                                                    )}
                                                    <Typography 
                                                        type="small_regular" 
                                                        text={`Due: ${new Date(subtask.deadline).toLocaleDateString()}`} 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {goalReviews.length > 0 && (
                            <div className="reviews-section">
                                <Typography type="base_medium" text="Previous Reviews" />
                                <div className="reviews-list">
                                    {goalReviews
                                        .filter(review => review.goalAssignmentGoalId === goalAssignmentGoalId)
                                        .sort((a, b) => new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime())
                                        .map(review => (
                                            <div key={review.id} className="review-item">
                                                <div className="review-header">
                                                    <ReactStars
                                                        count={5}
                                                        value={review.rating}
                                                        edit={false}
                                                        size={16}
                                                        activeColor="#ffd700"
                                                    />
                                                    <Typography type="small_regular" text={`by ${review.reviewerName} (${review.reviewerRole})`} />
                                                </div>
                                                <Typography type="base_regular" text={review.reviewText} />
                                                <Typography 
                                                    type="small_regular" 
                                                    text={new Date(review.reviewDate).toLocaleDateString()} 
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="add-review-section">
                        <Typography type="base_medium" text="Add Review" />
                        <div className="rating-input">
                            <Typography type="small_medium" text="Rating:" />
                            <ReactStars
                                count={5}
                                value={rating}
                                onChange={setRating}
                                size={24}
                                activeColor="#ffd700"
                            />
                        </div>
                        <div className="comment-input">
                            <Typography type="small_medium" text="Comment:" />
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Enter your review comment..."
                                rows={4}
                            />
                        </div>
                    </div>
                </div>

                <div className="review-modal__footer">
                    <Button type="secondary" size="base" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        size="base" 
                        onClick={handleSubmit}
                        disabled={addGoalReviewStatus === ApiResponseStatus.pending || reviewerIdLoading}
                    >
                        {addGoalReviewStatus === ApiResponseStatus.pending ? 'Submitting...' : 
                         reviewerIdLoading ? 'Loading...' : 'Submit Review'}
                    </Button>
                </div>
            </div>
        </div>
    );
}; 